<template>
  <v-dialog
    v-model="internalValue"
    max-width="900"
    transition="dialog-bottom-transition"
    scrollable
  >
    <v-card class="ds-dialog">
      <!-- Cabeçalho -->
      <v-card-title class="ds-dialog-header">
        <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-calendar-edit</v-icon>
        <span class="ds-dialog-title">Editar Atendimento</span>
        <v-spacer />
        <v-btn
          icon
          color="white"
          class="close-btn"
          aria-label="Ver tour deste formulário"
          @click="startTour"
        >
          <v-icon>mdi-help-circle-outline</v-icon>
        </v-btn>
        <v-btn icon color="white" class="close-btn" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <v-form ref="editForm" v-model="formValid">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-calendar-account</v-icon>
                <span>Atendimento</span>
              </div>
            </div>
            <v-row dense>
              <!-- Nome do Paciente -->
              <v-col cols="12" md="6">
                <v-text-field
                  label="Paciente"
                  v-model="editAppointment.patient"
                  prepend-inner-icon="mdi-account-outline"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  :rules="[(v) => !!v || 'Obrigatório']"
                  :disabled="true"
                />
              </v-col>

              <!-- Nome do Médico -->
              <v-col cols="12" md="6">
                <v-text-field
                  label="Nome do Médico"
                  v-model="doctorName"
                  prepend-inner-icon="mdi-stethoscope"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  :disabled="true"
                />
              </v-col>

              <!-- Data do Atendimento -->
              <v-col cols="12" sm="6" data-tour="ed-datahora">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDayDate(editAppointment.dayDate)"
                      label="Data do Atendimento"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      :rules="[() => !!editAppointment.dayDate || 'Obrigatório']"
                    />
                  </template>
                  <v-date-picker
                    v-model="editAppointment.dayDate"
                    :events="isWorkingDay"
                    event-color="#3E8E73"
                    :picker-date.sync="pickerMonth"
                    locale="pt-BR"
                    color="primary"
                    no-title
                    scrollable
                    class="agenda-highlight-picker"
                    @input="dateMenu = false"
                  ></v-date-picker>
                </v-menu>
                <div
                  v-if="availabilityHint"
                  class="caption grey--text text--darken-1 mt-1"
                >
                  {{ availabilityHint }}
                </div>
              </v-col>

              <!-- Horário do Atendimento -->
              <v-col cols="12" sm="6">
                <v-row dense>
                  <!-- Início -->
                  <v-col cols="6">
                    <v-autocomplete
                      v-model="editAppointment.beginTime"
                      :items="allowedBeginTimes"
                      label="Início"
                      prepend-inner-icon="mdi-clock-outline"
                      outlined
                      dense
                      hide-details="auto"
                      color="primary"
                      :rules="[(v) => !!v || 'Obrigatório']"
                      @change="onBeginTimeChange"
                    >
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                          v-bind="attrs"
                          v-on="on"
                          :class="[
                            'agenda-time-item',
                            { 'agenda-time-item--on': isWorkingTime(item) },
                          ]"
                        >
                          <v-list-item-title>{{ item }}</v-list-item-title>
                          <v-icon
                            v-if="isWorkingTime(item)"
                            small
                            class="agenda-time-item__check ml-2"
                          >mdi-check</v-icon>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <!-- Fim -->
                  <v-col cols="6">
                    <v-autocomplete
                      v-model="editAppointment.endTime"
                      :items="allowedEndTimes"
                      label="Fim"
                      prepend-inner-icon="mdi-clock-end"
                      outlined
                      dense
                      hide-details="auto"
                      color="primary"
                      :rules="[
                        (v) => !!v || 'Obrigatório',
                        (v) =>
                          v !== editAppointment.beginTime ||
                          'Horário de fim deve ser diferente do início',
                      ]"
                    >
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                          v-bind="attrs"
                          v-on="on"
                          :class="[
                            'agenda-time-item',
                            { 'agenda-time-item--on': isWorkingEndTime(item) },
                          ]"
                        >
                          <v-list-item-title>{{ item }}</v-list-item-title>
                          <v-icon
                            v-if="isWorkingEndTime(item)"
                            small
                            class="agenda-time-item__check ml-2"
                          >mdi-check</v-icon>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </section>

          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-list-status</v-icon>
                <span>Status</span>
              </div>
            </div>
            <v-row dense>
              <!-- Status do Atendimento -->
              <v-col cols="12" md="6" data-tour="ed-status">
                <v-select
                  v-model="editAppointment.status"
                  :items="statusOptions"
                  label="Status"
                  prepend-inner-icon="mdi-list-status"
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  :rules="[(v) => !!v || 'Obrigatório']"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip :color="item.color" text-color="white" small>
                      {{ item.text }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-chip :color="item.color" text-color="white" small>
                            {{ item.text }}
                          </v-chip>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </section>

          <!-- Observações -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-note-text</v-icon>
                <span>Observações</span>
                <span class="ds-form-section__optional">opcional</span>
              </div>
            </div>
            <v-textarea
              v-model="editAppointment.description"
              rows="2"
              auto-grow
              outlined
              dense
              hide-details="auto"
              color="primary"
              prepend-inner-icon="mdi-pencil"
            />
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" class="ds-cancel-btn" @click="closeDialog">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="ds-save-btn"
          data-tour="ed-salvar"
          dark
          depressed
          :loading="loading"
          @click="editAppointmentHandler"
        >
          <v-icon left>mdi-pencil</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import scheduleAvailabilityMixin from "@/features/calendar/mixins/scheduleAvailabilityMixin";
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import { userTimeZone } from "@/utils/datetime";

export default {
  name: "EditAppointmentDialog",

  mixins: [scheduleAvailabilityMixin, tourDialogMixin],

  props: {
    value: { type: Boolean, default: false },
    eventToEdit: {
      type: Object,
    },
  },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      dialogEditEvent: false,
      menuData: false,
      menuHora: false,

      doctorName: "",
      statusOptions: [
        { text: "Em espera", value: "waiting", color: "#FFC107" },
        { text: "Agendado", value: "scheduled", color: "#2196F3" },
        { text: "Confirmado", value: "confirmed", color: "#4CAF50" },
        // salvia-771: sem esta opção, abrir um evento marcado como falta no
        // kanban deixaria o campo de status em branco, e salvar apagaria a falta.
        { text: "Falta", value: "no_show", color: "#607D8B" },
        { text: "Cancelado", value: "canceled", color: "#F44336" },
        { text: "Compromisso", value: "blocked", color: "#EA580C" },
      ],
      loading: false,
      formValid: true,

      eventReadyToEdit: null,
      editAppointment: {
        id: null,
        google_id: null,
        patient: "",
        dayDate: null,
        beginTime: "",
        endTime: "",
        doctor: "",
        description: "",
        status: "scheduled",
      },
    };
  },

  watch: {
    eventToEdit: {
      handler(newVal) {
        this.eventReadyToEdit = newVal;
        if (newVal) {
          this.fillEditForm(newVal);
          // Carrega a disponibilidade do médico/clínica deste evento para
          // bloquear dias/horários no picker e nos selects.
          this.resetSchedule();
        }
      },
      immediate: true,
    },
    value(open) {
      if (open) this.tourOnOpen(); // tour guiado na 1ª vez que abre
    },
  },

  computed: {
    tourId() {
      return "dialog-editar-evento";
    },
    // ✅ usa Vuex: sem request
    userData() {
      return this.$store.getters["auth/user"];
    },

    // ── Contrato do scheduleAvailabilityMixin ──────────────────────────
    // A clínica/médico vêm do próprio evento sendo editado.
    scheduleClinicId() {
      return this.eventToEdit?.clinic_id || null;
    },
    scheduleDoctorId() {
      return this.eventToEdit?.doctor_id || this.userData?.id || null;
    },
    scheduleDate() {
      return this.editAppointment.dayDate || null;
    },
    scheduleBeginTime() {
      return this.editAppointment.beginTime || "";
    },
    // scheduleAllTimes agora vem do mixin (fonte única em buildTimeGrid()).

    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  methods: {
    // Tour guiado (tourDialogMixin fornece tourOnOpen/startTour).
    tourReady() {
      return !!this.eventReadyToEdit;
    },
    tourSteps() {
      return [
        {
          anchor: "ed-datahora",
          title: "Ajuste data e horário",
          description:
            "Reagende o atendimento alterando o dia e os horários. Os dias/horários de atendimento do médico ficam destacados em verde.",
        },
        {
          anchor: "ed-status",
          title: "Mude o status",
          description:
            "Marque o atendimento como Em espera, Agendado, Confirmado, Cancelado ou Bloqueado — é o que organiza a sua agenda e o Kanban.",
        },
        {
          anchor: "ed-salvar",
          title: "Salvar as alterações",
          description:
            "Confirme para atualizar o atendimento. O convite dos participantes é atualizado automaticamente.",
        },
      ];
    },

    // --------------------------
    // TIMEZONE HELPERS
    // --------------------------
    getBrowserTimeZone: userTimeZone,

    getEventTimeZone(event) {
      return event?.time_zone || this.getBrowserTimeZone();
    },

    buildLocalDateTime(dayDate, timeHHmm) {
      const [y, m, d] = (dayDate || "").split("-").map(Number);
      const [hh, mm] = (timeHHmm || "").split(":").map(Number);
      return new Date(y, m - 1, d, hh, mm, 0, 0);
    },

    formatDateInTimeZone(isoOrDateString, timeZone) {
      const dt = new Date(isoOrDateString);
      return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(dt);
    },

    formatTimeInTimeZone(isoOrDateString, timeZone) {
      const dt = new Date(isoOrDateString);
      return new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(dt);
    },

    getStartUtcIso() {
      return this.buildLocalDateTime(
        this.editAppointment.dayDate,
        this.editAppointment.beginTime,
      ).toISOString();
    },

    getEndUtcIso() {
      return this.buildLocalDateTime(
        this.editAppointment.dayDate,
        this.editAppointment.endTime,
      ).toISOString();
    },

    buildEventData() {
      const tz = this.getEventTimeZone(this.eventReadyToEdit);

      return {
        event_id: this.editAppointment.google_id,
        summary: this.editAppointment.patient,
        start_date: this.getStartUtcIso(),
        end_date: this.getEndUtcIso(),
        time_zone: tz,
        description: this.editAppointment.description || "",
        status: this.editAppointment.status,
      };
    },

    // --------------------------
    // UI
    // --------------------------
    onBeginTimeChange() {
      this.editAppointment.endTime = this.computeEndTime(
        this.editAppointment.beginTime,
      );
    },

    async openAddDialog() {
      // ✅ Garante que o Vuex carregou o user (no máximo 1 request /api/user por sessão)
      try {
        if (this.$store.getters["auth/isAuthenticated"]) {
          await this.$store.dispatch("auth/ensureAuth");
        }
      } catch {
        // se falhar, o router guard normalmente já impediria estar aqui
      }

      // ✅ Nome do médico vem do Vuex (sem request)
      this.doctorName = this.userData?.name || "";
      this.editAppointment.doctor = this.userData?.email || "";

      this.dialogEditEvent = true;
    },

    closeDialog() {
      this.$emit("input", false);
    },

    fillEditForm(event) {
      this.editAppointment.id = event.id;
      this.editAppointment.google_id = event.google_id;
      this.editAppointment.patient = event.name;

      const tz = this.getEventTimeZone(event);

      if (event.start_iso && event.end_iso) {
        this.editAppointment.dayDate = this.formatDateInTimeZone(
          event.start_iso,
          tz,
        );

        this.editAppointment.beginTime = this.formatTimeInTimeZone(
          event.start_iso,
          tz,
        );

        this.editAppointment.endTime = this.formatTimeInTimeZone(
          event.end_iso,
          tz,
        );
      }

      this.editAppointment.description = event.description || "";
      this.editAppointment.status = event.status || "scheduled";
    },

    async editAppointmentHandler() {
      try {
        this.loading = true;

        // opcional: validação igual ao create
        // const isValid = await this.$refs.editForm.validate();
        // if (!isValid) { this.error("Preencha os campos obrigatórios."); return; }

        const eventData = this.buildEventData();

        // ✅ usa http (Bearer automático)
        await http.put("/api/google-calendar/edit-event", eventData);

        this.success("Atendimento editado com sucesso!");
        this.closeDialog();
        this.$emit("eventEdited");
      } catch (err) {
        console.error("Erro ao editar atendimento:", err);
        // O backend valida a disponibilidade: mostra a mensagem dele no 422.
        const serverMessage = err.response?.data?.message;
        this.erroDeRequisicao(err, 
          err.response?.status === 422 && serverMessage
            ? serverMessage
            : "Erro ao editar atendimento. Tente novamente.",
        );
      } finally {
        this.loading = false;
      }
    },

  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Action buttons ═══ */
.ds-cancel-btn,
.ds-save-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  height: 40px !important;
}

.ds-save-btn {
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.22) !important;
}

</style>

<!--
  Destaque (não bloqueio) da disponibilidade do médico.
  NÃO pode ser scoped: o menu do autocomplete e o date picker são anexados fora
  do componente (em [data-app]), então regras scoped não os alcançam.
-->
<style>
/* Horário em que o médico atende → linha destacada em verde */
.agenda-time-item.agenda-time-item--on {
  background-color: rgba(62, 142, 115, 0.16) !important;
  border-left: 3px solid var(--ds-primary, #3e8e73);
}
.agenda-time-item.agenda-time-item--on .v-list-item__title {
  font-weight: 700;
}
.agenda-time-item__check {
  color: var(--ds-primary, #3e8e73) !important;
}

/* Dia de atendimento: FUNDO verde preenchendo o dia (em vez do pontinho).
   No Vuetify 2.7 o marcador é .v-date-picker-table__events > div. */
.agenda-highlight-picker .v-date-picker-table__events {
  top: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  line-height: 0;
  pointer-events: none;
  z-index: 0;
}
.agenda-highlight-picker .v-date-picker-table__events > div {
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 50% !important;
  background-color: rgba(62, 142, 115, 0.24) !important;
}
.agenda-highlight-picker .v-date-picker-table .v-btn__content {
  position: relative;
  z-index: 1;
}
/* O dia selecionado fica sólido (esconde o destaque atrás dele) */
.agenda-highlight-picker
  .v-date-picker-table
  .v-btn--active
  .v-date-picker-table__events {
  display: none;
}
</style>
